import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { useToast } from '../hooks/useToast';
import api from '../services/api';

const الإعدادات = () => {
  const { showToast, ToastContainer } = useToast();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [countryForm, setCountryForm] = useState({
    name: '',
    imageFile: null,
    logoFile: null,
    imagePreview: null,
    logoPreview: null
  });
  const [savingCountry, setSavingCountry] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState(() => {
    // استرجاع الحالة المحفوظة من localStorage
    const saved = localStorage.getItem('whatsappStatus');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          connected: parsed.connected || false,
          qrCode: parsed.qrCode || null,
          loading: true, // دائماً نبدأ بـ loading
          resetting: false,
          message: parsed.message || ''
        };
      } catch (e) {
        console.error('Error parsing saved WhatsApp status:', e);
      }
    }
    return {
      connected: false,
      qrCode: null,
      loading: true,
      resetting: false,
      message: ''
    };
  });
  const [settings, setSettings] = useState({
    siteName: 'Min Bayty',
    siteDescription: 'منصة طلب الطعام',
    siteKeywords: 'طعام، مطاعم، توصيل',
    siteAddress: 'الرياض، المملكة العربية السعودية',
    email: 'info@minbayty.com',
    phone: '+966 50 123 4567',
    supportEmail: 'support@minbayty.com',
    whatsapp: '+966 50 123 4567',
    language: 'ar',
    timezone: 'Asia/Riyadh',
    currency: 'SAR',
    taxRate: '15',
    deliveryFee: '10',
    minOrder: '50',
    maintenance: false,
    notifications: true,
    emailNotifications: true,
    smsNotifications: false,
    // ✅ إعدادات التطبيق
    isOtpEnabled: true, // ✅ تفعيل/تعطيل التحقق من الهاتف (OTP)
    maintenanceMode: false,
    maintenanceMessage: 'نعمل على تحسين التطبيق لتقديم تجربة أفضل. سنعود قريباً!',
    // Android settings
    updateRequiredAndroid: false,
    updateMessageAndroid: 'يتوفر إصدار جديد من التطبيق. يرجى التحديث للمتابعة.',
    updateUrlAndroid: '',
    minimumAppVersionAndroid: '1.0.0+1',
    // iOS settings
    updateRequiredIOS: false,
    updateMessageIOS: 'يتوفر إصدار جديد من التطبيق. يرجى التحديث للمتابعة.',
    updateUrlIOS: '',
    minimumAppVersionIOS: '1.0.0+1',
    // Legacy (for backward compatibility)
    updateRequired: false,
    updateMessage: 'يتوفر إصدار جديد من التطبيق. يرجى التحديث للمتابعة.',
    updateUrl: '',
    minimumAppVersion: '1.0.0+1'
  });

  useEffect(() => {
    fetchSettings();
    fetchCountries();
    fetchWhatsAppStatus();

    // تحديث حالة WhatsApp كل 3 ثواني (أسرع للحصول على QR Code)
    // لكن فقط إذا لم يكن متصل (لتوفير استدعاءات API)
    const interval = setInterval(() => {
      setWhatsappStatus(prev => {
        // إذا كان متصل، لا نحتاج تحديث متكرر
        if (prev.connected) {
          // تحديث كل 30 ثانية فقط عند الاتصال
          return prev;
        }
        // إذا لم يكن متصل، نحدث كل 3 ثواني للحصول على QR Code
        fetchWhatsAppStatus();
        return prev;
      });
    }, 3000);

    // تحديث دوري للحالة عند الاتصال (كل 30 ثانية)
    const connectedInterval = setInterval(() => {
      setWhatsappStatus(prev => {
        if (prev.connected) {
          fetchWhatsAppStatus();
        }
        return prev;
      });
    }, 30000);

    return () => {
      clearInterval(interval);
      clearInterval(connectedInterval);
    };
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await api.getSettings();
      if (data) {
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Fallback to localStorage if API fails
      const savedSettings = localStorage.getItem('dashboardSettings');
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings);
          setSettings(prev => ({ ...prev, ...parsed }));
        } catch (parseError) {
          console.error('Error loading settings from localStorage:', parseError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await api.updateSettings(settings);
      if (result.success) {
        // Also save to localStorage as backup
        localStorage.setItem('dashboardSettings', JSON.stringify(settings));
        showToast(result.message || 'تم حفظ الإعدادات بنجاح', 'success');
      } else {
        showToast(result.message || 'حدث خطأ في حفظ الإعدادات', 'error');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      // Fallback to localStorage if API fails
      try {
        localStorage.setItem('dashboardSettings', JSON.stringify(settings));
        showToast('تم حفظ الإعدادات محلياً (حدث خطأ في الاتصال بالخادم)', 'warning');
      } catch (localError) {
        showToast('حدث خطأ في حفظ الإعدادات', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const data = await api.getCountries();
      setCountries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching countries:', error);
      showToast('حدث خطأ في جلب البلدان', 'error');
    } finally {
      setLoadingCountries(false);
    }
  };

  const handleAddCountry = () => {
    setEditingCountry(null);
    setCountryForm({
      name: '',
      imageFile: null,
      logoFile: null,
      imagePreview: null,
      logoPreview: null
    });
    setShowCountryModal(true);
  };

  const handleEditCountry = (country) => {
    setEditingCountry(country);
    setCountryForm({
      name: country.name || '',
      imageFile: null,
      logoFile: null,
      imagePreview: country.imageUrl || null,
      logoPreview: country.logoImage || null
    });
    setShowCountryModal(true);
  };

  const handleCountryFormChange = (field, value) => {
    if (field === 'imageFile' || field === 'logoFile') {
      const file = value;
      const previewField = field === 'imageFile' ? 'imagePreview' : 'logoPreview';
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setCountryForm(prev => ({
            ...prev,
            [field]: file,
            [previewField]: reader.result
          }));
        };
        reader.readAsDataURL(file);
      } else {
        setCountryForm(prev => ({
          ...prev,
          [field]: null,
          [previewField]: null
        }));
      }
    } else {
      setCountryForm(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSaveCountry = async () => {
    if (!countryForm.name.trim()) {
      showToast('يرجى إدخال اسم البلد', 'error');
      return;
    }

    setSavingCountry(true);
    try {
      if (editingCountry) {
        await api.updateCountry(
          editingCountry.id,
          { name: countryForm.name },
          countryForm.imageFile,
          countryForm.logoFile
        );
        showToast('تم تحديث البلد بنجاح', 'success');
      } else {
        await api.createCountry(
          { name: countryForm.name },
          countryForm.imageFile,
          countryForm.logoFile
        );
        showToast('تم إضافة البلد بنجاح', 'success');
      }
      setShowCountryModal(false);
      fetchCountries();
    } catch (error) {
      console.error('Error saving country:', error);
      showToast(error.message || 'حدث خطأ في حفظ البلد', 'error');
    } finally {
      setSavingCountry(false);
    }
  };

  const handleDeleteCountry = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا البلد؟')) {
      return;
    }

    try {
      await api.deleteCountry(id);
      showToast('تم حذف البلد بنجاح', 'success');
      fetchCountries();
    } catch (error) {
      console.error('Error deleting country:', error);
      showToast(error.message || 'حدث خطأ في حذف البلد', 'error');
    }
  };

  const fetchWhatsAppStatus = async () => {
    try {
      setWhatsappStatus(prev => ({ ...prev, loading: true }));

      const info = await api.getWhatsAppInfo();

      const newStatus = {
        connected: info.connected || false,
        qrCode: info.qrCode || null,
        loading: false,
        resetting: false,
        message: info.message || ''
      };

      // حفظ الحالة في localStorage
      localStorage.setItem('whatsappStatus', JSON.stringify({
        connected: newStatus.connected,
        qrCode: newStatus.qrCode,
        message: newStatus.message
      }));

      setWhatsappStatus(newStatus);
    } catch (error) {
      console.error('Error fetching WhatsApp status:', error);
      const errorStatus = {
        connected: false,
        qrCode: null,
        loading: false,
        resetting: false,
        message: 'خطأ في الاتصال بالخادم'
      };

      // حفظ حالة الخطأ
      localStorage.setItem('whatsappStatus', JSON.stringify({
        connected: false,
        qrCode: null,
        message: errorStatus.message
      }));

      setWhatsappStatus(errorStatus);
    }
  };

  const handleResetWhatsApp = async () => {
    if (!window.confirm('هل أنت متأكد من إعادة تهيئة اتصال WhatsApp؟ سيتم قطع الاتصال الحالي وتوليد QR Code جديد.')) {
      return;
    }

    setWhatsappStatus({
      connected: false,
      qrCode: null,
      loading: true,
      resetting: true,
      message: 'جاري إعادة التهيئة...'
    });

    // حذف الحالة المحفوظة
    localStorage.removeItem('whatsappStatus');

    try {
      await api.resetWhatsAppConnection();
      showToast('تم إعادة تهيئة WhatsApp. انتظر QR Code جديد...', 'success');

      // تحديث الحالة عدة مرات للحصول على QR Code
      setTimeout(() => fetchWhatsAppStatus(), 2000);
      setTimeout(() => fetchWhatsAppStatus(), 5000);
      setTimeout(() => fetchWhatsAppStatus(), 8000);
      setTimeout(() => fetchWhatsAppStatus(), 12000);
    } catch (error) {
      console.error('Error resetting WhatsApp:', error);
      showToast(error.message || 'حدث خطأ في إعادة تهيئة WhatsApp', 'error');
      setWhatsappStatus(prev => ({
        ...prev,
        loading: false,
        resetting: false,
        message: 'فشل إعادة التهيئة'
      }));
    }
  };

  const handleRefreshQR = async () => {
    setWhatsappStatus(prev => ({ ...prev, loading: true, message: 'جاري تحديث QR Code...' }));
    await fetchWhatsAppStatus();
  };

  const handleReset = async () => {
    try {
      // Fetch default settings from backend
      const defaultSettings = await api.getSettings();
      setSettings(defaultSettings);
      localStorage.removeItem('dashboardSettings');
      showToast('تم إعادة تعيين الإعدادات', 'success');
    } catch (error) {
      console.error('Error resetting settings:', error);
      // Fallback to hardcoded defaults
      const defaultSettings = {
        siteName: 'Min Bayty',
        siteDescription: 'منصة طلب الطعام',
        siteKeywords: 'طعام، مطاعم، توصيل',
        siteAddress: 'الرياض، المملكة العربية السعودية',
        email: 'info@minbayty.com',
        phone: '+966 50 123 4567',
        supportEmail: 'support@minbayty.com',
        whatsapp: '+966 50 123 4567',
        language: 'ar',
        timezone: 'Asia/Riyadh',
        currency: 'SAR',
        taxRate: '15',
        deliveryFee: '10',
        minOrder: '50',
        maintenance: false,
        notifications: true,
        emailNotifications: true,
        smsNotifications: false,
        isOtpEnabled: true,
        maintenanceMode: false,
        maintenanceMessage: 'نعمل على تحسين التطبيق لتقديم تجربة أفضل. سنعود قريباً!',
        // Android settings
        updateRequiredAndroid: false,
        updateMessageAndroid: 'يتوفر إصدار جديد من التطبيق. يرجى التحديث للمتابعة.',
        updateUrlAndroid: '',
        minimumAppVersionAndroid: '1.0.0+1',
        // iOS settings
        updateRequiredIOS: false,
        updateMessageIOS: 'يتوفر إصدار جديد من التطبيق. يرجى التحديث للمتابعة.',
        updateUrlIOS: '',
        minimumAppVersionIOS: '1.0.0+1',
        // Legacy
        updateRequired: false,
        updateMessage: 'يتوفر إصدار جديد من التطبيق. يرجى التحديث للمتابعة.',
        updateUrl: '',
        minimumAppVersion: '1.0.0+1'
      };
      setSettings(defaultSettings);
      localStorage.removeItem('dashboardSettings');
      showToast('تم إعادة تعيين الإعدادات', 'success');
    }
  };

  return (
    <div style={{
      width: '100%',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#2B2F3F',
      overflow: 'hidden',
      direction: 'rtl'
    }}>
      <Header />
      <div style={{
        flex: 1,
        padding: '32px',
        backgroundColor: '#2B2F3F',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        boxSizing: 'border-box'
      }}>
        {/* Page Title */}
        <h1 style={{
          fontFamily: 'El Messiri, sans-serif',
          fontSize: '24px',
          fontWeight: 700,
          color: '#FFFFFF',
          margin: '0 0 8px 0',
          textAlign: 'right'
        }}>
          الإعدادات
        </h1>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '24px',
          width: '100%'
        }}>
          {/* Site Settings */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid rgba(233, 106, 58, 0.15)',
            boxShadow: '0px 4px 23px rgba(0, 0, 0, 0.04)',
            boxSizing: 'border-box'
          }}>
            <h3 style={{
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '16px',
              fontWeight: 700,
              color: '#2B2F3F',
              marginBottom: '20px',
              textAlign: 'right',
              margin: '0 0 20px 0',
              paddingBottom: '12px',
              borderBottom: '2px solid rgba(233, 106, 58, 0.1)'
            }}>
              إعدادات الموقع
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  اسم الموقع
                </label>
                <input
                  type="text"
                  value={settings.siteName}
                  onChange={(e) => handleChange('siteName', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  dir="rtl"
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  وصف الموقع
                </label>
                <textarea
                  value={settings.siteDescription}
                  onChange={(e) => handleChange('siteDescription', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    minHeight: '80px',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                  dir="rtl"
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  الكلمات المفتاحية
                </label>
                <input
                  type="text"
                  value={settings.siteKeywords}
                  onChange={(e) => handleChange('siteKeywords', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  dir="rtl"
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  العنوان
                </label>
                <input
                  type="text"
                  value={settings.siteAddress}
                  onChange={(e) => handleChange('siteAddress', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  dir="rtl"
                />
              </div>
            </div>
          </div>

          {/* Contact Settings */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid rgba(233, 106, 58, 0.15)',
            boxShadow: '0px 4px 23px rgba(0, 0, 0, 0.04)',
            boxSizing: 'border-box'
          }}>
            <h3 style={{
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '16px',
              fontWeight: 700,
              color: '#2B2F3F',
              marginBottom: '20px',
              textAlign: 'right',
              margin: '0 0 20px 0',
              paddingBottom: '12px',
              borderBottom: '2px solid rgba(233, 106, 58, 0.1)'
            }}>
              معلومات الاتصال
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  البريد الإلكتروني الرئيسي
                </label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  dir="ltr"
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  بريد الدعم الفني
                </label>
                <input
                  type="email"
                  value={settings.supportEmail}
                  onChange={(e) => handleChange('supportEmail', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  dir="ltr"
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  رقم الهاتف
                </label>
                <input
                  type="tel"
                  value={settings.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  dir="ltr"
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  واتساب
                </label>
                <input
                  type="tel"
                  value={settings.whatsapp}
                  onChange={(e) => handleChange('whatsapp', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  dir="ltr"
                />
              </div>
            </div>
          </div>

          {/* General Settings */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid rgba(233, 106, 58, 0.15)',
            boxShadow: '0px 4px 23px rgba(0, 0, 0, 0.04)',
            boxSizing: 'border-box'
          }}>
            <h3 style={{
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '16px',
              fontWeight: 700,
              color: '#2B2F3F',
              marginBottom: '20px',
              textAlign: 'right',
              margin: '0 0 20px 0',
              paddingBottom: '12px',
              borderBottom: '2px solid rgba(233, 106, 58, 0.1)'
            }}>
              الإعدادات العامة
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  اللغة
                </label>
                <select
                  value={settings.language}
                  onChange={(e) => handleChange('language', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: '#FFFFFF',
                    boxSizing: 'border-box'
                  }}
                  dir="rtl"
                >
                  <option value="ar">العربية</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  المنطقة الزمنية
                </label>
                <select
                  value={settings.timezone}
                  onChange={(e) => handleChange('timezone', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: '#FFFFFF',
                    boxSizing: 'border-box'
                  }}
                  dir="rtl"
                >
                  <option value="Asia/Riyadh">الرياض</option>
                  <option value="Asia/Dubai">دبي</option>
                  <option value="Asia/Kuwait">الكويت</option>
                </select>
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  العملة
                </label>
                <select
                  value={settings.currency}
                  onChange={(e) => handleChange('currency', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: '#FFFFFF',
                    boxSizing: 'border-box'
                  }}
                  dir="rtl"
                >
                  <option value="SAR">ريال سعودي</option>
                  <option value="USD">دولار أمريكي</option>
                  <option value="EUR">يورو</option>
                </select>
              </div>
            </div>
          </div>

          {/* Payment Settings */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid rgba(233, 106, 58, 0.15)',
            boxShadow: '0px 4px 23px rgba(0, 0, 0, 0.04)',
            boxSizing: 'border-box'
          }}>
            <h3 style={{
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '16px',
              fontWeight: 700,
              color: '#2B2F3F',
              marginBottom: '20px',
              textAlign: 'right',
              margin: '0 0 20px 0',
              paddingBottom: '12px',
              borderBottom: '2px solid rgba(233, 106, 58, 0.1)'
            }}>
              إعدادات الدفع والطلبات
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  نسبة الضريبة (%)
                </label>
                <input
                  type="number"
                  value={settings.taxRate}
                  onChange={(e) => handleChange('taxRate', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  dir="rtl"
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  رسوم التوصيل (ريال)
                </label>
                <input
                  type="number"
                  value={settings.deliveryFee}
                  onChange={(e) => handleChange('deliveryFee', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  dir="rtl"
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  الحد الأدنى للطلب (ريال)
                </label>
                <input
                  type="number"
                  value={settings.minOrder}
                  onChange={(e) => handleChange('minOrder', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  dir="rtl"
                />
              </div>
            </div>
          </div>

          {/* Notification Settings */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid rgba(233, 106, 58, 0.15)',
            boxShadow: '0px 4px 23px rgba(0, 0, 0, 0.04)',
            boxSizing: 'border-box'
          }}>
            <h3 style={{
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '16px',
              fontWeight: 700,
              color: '#2B2F3F',
              marginBottom: '20px',
              textAlign: 'right',
              margin: '0 0 20px 0',
              paddingBottom: '12px',
              borderBottom: '2px solid rgba(233, 106, 58, 0.1)'
            }}>
              إعدادات الإشعارات
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{
                  width: '48px',
                  height: '26px',
                  borderRadius: '13px',
                  border: 'none',
                  backgroundColor: settings.notifications ? '#3FB984' : 'rgba(176, 179, 187, 0.3)',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.3s'
                }}
                  onClick={() => handleChange('notifications', !settings.notifications)}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#FFFFFF',
                    position: 'absolute',
                    top: '3px',
                    right: settings.notifications ? '3px' : '25px',
                    transition: 'all 0.3s',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)'
                  }} />
                </div>
                <label style={{
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  cursor: 'pointer'
                }}
                  onClick={() => handleChange('notifications', !settings.notifications)}
                >
                  تفعيل الإشعارات
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{
                  width: '48px',
                  height: '26px',
                  borderRadius: '13px',
                  border: 'none',
                  backgroundColor: settings.emailNotifications ? '#3FB984' : 'rgba(176, 179, 187, 0.3)',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.3s'
                }}
                  onClick={() => handleChange('emailNotifications', !settings.emailNotifications)}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#FFFFFF',
                    position: 'absolute',
                    top: '3px',
                    right: settings.emailNotifications ? '3px' : '25px',
                    transition: 'all 0.3s',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)'
                  }} />
                </div>
                <label style={{
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  cursor: 'pointer'
                }}
                  onClick={() => handleChange('emailNotifications', !settings.emailNotifications)}
                >
                  إشعارات البريد الإلكتروني
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{
                  width: '48px',
                  height: '26px',
                  borderRadius: '13px',
                  border: 'none',
                  backgroundColor: settings.smsNotifications ? '#3FB984' : 'rgba(176, 179, 187, 0.3)',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.3s'
                }}
                  onClick={() => handleChange('smsNotifications', !settings.smsNotifications)}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#FFFFFF',
                    position: 'absolute',
                    top: '3px',
                    right: settings.smsNotifications ? '3px' : '25px',
                    transition: 'all 0.3s',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)'
                  }} />
                </div>
                <label style={{
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  cursor: 'pointer'
                }}
                  onClick={() => handleChange('smsNotifications', !settings.smsNotifications)}
                >
                  إشعارات الرسائل النصية
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid rgba(0, 0, 0, 0.06)' }}>
                <div style={{
                  width: '48px',
                  height: '26px',
                  borderRadius: '13px',
                  border: 'none',
                  backgroundColor: settings.maintenance ? '#F27D72' : 'rgba(176, 179, 187, 0.3)',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.3s'
                }}
                  onClick={() => handleChange('maintenance', !settings.maintenance)}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#FFFFFF',
                    position: 'absolute',
                    top: '3px',
                    right: settings.maintenance ? '3px' : '25px',
                    transition: 'all 0.3s',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)'
                  }} />
                </div>
                <label style={{
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  cursor: 'pointer'
                }}
                  onClick={() => handleChange('maintenance', !settings.maintenance)}
                >
                  وضع الصيانة
                </label>
              </div>
            </div>
          </div>

          {/* Verification Settings */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid rgba(233, 106, 58, 0.15)',
            boxShadow: '0px 4px 23px rgba(0, 0, 0, 0.04)',
            boxSizing: 'border-box'
          }}>
            <h3 style={{
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '16px',
              fontWeight: 700,
              color: '#2B2F3F',
              marginBottom: '20px',
              textAlign: 'right',
              margin: '0 0 20px 0',
              paddingBottom: '12px',
              borderBottom: '2px solid rgba(233, 106, 58, 0.1)'
            }}>
              إعدادات التحقق والأمان
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{
                  width: '48px',
                  height: '26px',
                  borderRadius: '13px',
                  border: 'none',
                  backgroundColor: settings.isOtpEnabled ? '#3FB984' : 'rgba(176, 179, 187, 0.3)',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.3s'
                }}
                  onClick={() => handleChange('isOtpEnabled', !settings.isOtpEnabled)}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#FFFFFF',
                    position: 'absolute',
                    top: '3px',
                    right: settings.isOtpEnabled ? '3px' : '25px',
                    transition: 'all 0.3s',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)'
                  }} />
                </div>
                <div style={{ flex: 1, marginRight: '16px' }}>
                  <label style={{
                    display: 'block',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#2B2F3F',
                    cursor: 'pointer',
                    marginBottom: '4px'
                  }}
                    onClick={() => handleChange('isOtpEnabled', !settings.isOtpEnabled)}
                  >
                    تفعيل التحقق عبر OTP (واتساب/رسائل نصية)
                  </label>
                  <span style={{
                    display: 'block',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '12px',
                    color: '#666A7A'
                  }}>
                    عند تعطيل هذا الخيار، سيتمكن المستخدمون من التسجيل دون الحاجة لتأكيد رقم الهاتف عبر رمز التحقق. مفيد لمراجعة آب ستور.
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* WhatsApp Settings */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid rgba(233, 106, 58, 0.15)',
            boxShadow: '0px 4px 23px rgba(0, 0, 0, 0.04)',
            boxSizing: 'border-box'
          }}>
            <h3 style={{
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '16px',
              fontWeight: 700,
              color: '#2B2F3F',
              marginBottom: '20px',
              textAlign: 'right',
              margin: '0 0 20px 0',
              paddingBottom: '12px',
              borderBottom: '2px solid rgba(233, 106, 58, 0.1)'
            }}>
              إعدادات WhatsApp
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Connection Status */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px',
                borderRadius: '12px',
                backgroundColor: whatsappStatus.connected ? 'rgba(63, 185, 132, 0.1)' : 'rgba(242, 125, 114, 0.1)',
                border: `2px solid ${whatsappStatus.connected ? '#3FB984' : '#F27D72'}`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: whatsappStatus.connected ? '#3FB984' : '#F27D72',
                    boxShadow: whatsappStatus.connected
                      ? '0 0 8px rgba(63, 185, 132, 0.5)'
                      : '0 0 8px rgba(242, 125, 114, 0.5)'
                  }}></div>
                  <div>
                    <div style={{
                      fontFamily: 'El Messiri, sans-serif',
                      fontSize: '15px',
                      fontWeight: 700,
                      color: '#2B2F3F',
                      marginBottom: '4px'
                    }}>
                      {whatsappStatus.connected ? 'WhatsApp متصل ✅' : 'WhatsApp غير متصل ❌'}
                    </div>
                    <div style={{
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '13px',
                      color: '#666A7A'
                    }}>
                      {whatsappStatus.loading
                        ? (whatsappStatus.resetting ? 'جاري إعادة التهيئة...' : 'جاري التحقق من الحالة...')
                        : whatsappStatus.message || (whatsappStatus.connected
                          ? 'يمكنك إرسال OTP والرسائل عبر WhatsApp'
                          : 'يرجى مسح QR Code للاتصال')}
                    </div>
                  </div>
                </div>
                {whatsappStatus.loading && (
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid rgba(43, 47, 63, 0.2)',
                    borderTop: '2px solid #E96A3A',
                    borderRadius: '50%',
                    animation: 'spin 0.6s linear infinite'
                  }}></div>
                )}
              </div>

              {/* QR Code Display */}
              {!whatsappStatus.connected && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '16px',
                  padding: '20px',
                  borderRadius: '12px',
                  backgroundColor: '#F9F9F9',
                  border: '1px solid rgba(0, 0, 0, 0.06)'
                }}>
                  {whatsappStatus.qrCode ? (
                    <>
                      <div style={{
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#2B2F3F',
                        textAlign: 'center'
                      }}>
                        امسح QR Code باستخدام WhatsApp على هاتفك
                      </div>
                      <div style={{
                        padding: '16px',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
                      }}>
                        <img
                          src={whatsappStatus.qrCode}
                          alt="WhatsApp QR Code"
                          style={{
                            width: '250px',
                            height: '250px',
                            display: 'block'
                          }}
                        />
                      </div>
                      <div style={{
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '12px',
                        color: '#666A7A',
                        textAlign: 'center',
                        maxWidth: '300px'
                      }}>
                        افتح WhatsApp → الإعدادات → الأجهزة المرتبطة → ربط جهاز
                      </div>
                      <button
                        onClick={handleRefreshQR}
                        disabled={whatsappStatus.loading}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          border: '1px solid rgba(233, 106, 58, 0.3)',
                          backgroundColor: 'transparent',
                          color: '#E96A3A',
                          fontFamily: 'Cairo, sans-serif',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: whatsappStatus.loading ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 4v6h6M23 20v-6h-6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        تحديث QR Code
                      </button>
                    </>
                  ) : (
                    <div style={{
                      padding: '20px',
                      textAlign: 'center',
                      width: '100%'
                    }}>
                      <div style={{
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '14px',
                        color: '#666A7A',
                        marginBottom: '16px',
                        textAlign: 'center'
                      }}>
                        {whatsappStatus.loading
                          ? (whatsappStatus.resetting
                            ? 'جاري إعادة التهيئة وتوليد QR Code جديد...'
                            : 'جاري التحقق من QR Code...')
                          : whatsappStatus.message || 'لا يوجد QR Code حالياً. اضغط على "إعادة التهيئة" لتوليد QR Code جديد.'}
                      </div>
                      {(whatsappStatus.loading || whatsappStatus.resetting) && (
                        <div style={{
                          width: '50px',
                          height: '50px',
                          border: '4px solid rgba(233, 106, 58, 0.2)',
                          borderTop: '4px solid #E96A3A',
                          borderRadius: '50%',
                          animation: 'spin 0.8s linear infinite',
                          margin: '0 auto'
                        }}></div>
                      )}
                      {!whatsappStatus.loading && !whatsappStatus.qrCode && !whatsappStatus.connected && (
                        <div style={{
                          fontFamily: 'Cairo, sans-serif',
                          fontSize: '12px',
                          color: '#E96A3A',
                          textAlign: 'center',
                          marginTop: '12px',
                          padding: '12px',
                          backgroundColor: 'rgba(233, 106, 58, 0.1)',
                          borderRadius: '8px'
                        }}>
                          💡 نصيحة: اضغط على "إعادة تهيئة WhatsApp" لتوليد QR Code جديد
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                {!whatsappStatus.connected && (
                  <button
                    onClick={handleRefreshQR}
                    disabled={whatsappStatus.loading}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '10px',
                      border: '1px solid rgba(233, 106, 58, 0.3)',
                      backgroundColor: 'transparent',
                      color: '#E96A3A',
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: whatsappStatus.loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 4v6h6M23 20v-6h-6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    تحديث QR Code
                  </button>
                )}
                <button
                  onClick={handleResetWhatsApp}
                  disabled={whatsappStatus.resetting || whatsappStatus.loading}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    backgroundColor: whatsappStatus.resetting ? '#cccccc' : '#E96A3A',
                    color: '#FFFFFF',
                    fontFamily: 'El Messiri, sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: (whatsappStatus.resetting || whatsappStatus.loading) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s',
                    opacity: (whatsappStatus.resetting || whatsappStatus.loading) ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!whatsappStatus.resetting && !whatsappStatus.loading) {
                      e.currentTarget.style.backgroundColor = '#d85a2a';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!whatsappStatus.resetting && !whatsappStatus.loading) {
                      e.currentTarget.style.backgroundColor = '#E96A3A';
                    }
                  }}
                >
                  {whatsappStatus.resetting ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid #FFFFFF',
                        borderRadius: '50%',
                        animation: 'spin 0.6s linear infinite'
                      }}></div>
                      جاري إعادة التهيئة...
                    </>
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 4v6h6M23 20v-6h-6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      إعادة تهيئة WhatsApp
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* App Settings */}
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            border: '1px solid rgba(233, 106, 58, 0.15)',
            boxShadow: '0px 4px 23px rgba(0, 0, 0, 0.04)',
            boxSizing: 'border-box'
          }}>
            <h3 style={{
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '16px',
              fontWeight: 700,
              color: '#2B2F3F',
              marginBottom: '20px',
              textAlign: 'right',
              margin: '0 0 20px 0',
              paddingBottom: '12px',
              borderBottom: '2px solid rgba(233, 106, 58, 0.1)'
            }}>
              إعدادات التطبيق
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Maintenance Mode */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '16px', borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}>
                <div style={{
                  width: '48px',
                  height: '26px',
                  borderRadius: '13px',
                  border: 'none',
                  backgroundColor: settings.maintenanceMode ? '#F27D72' : 'rgba(176, 179, 187, 0.3)',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.3s'
                }}
                  onClick={() => handleChange('maintenanceMode', !settings.maintenanceMode)}
                >
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    backgroundColor: '#FFFFFF',
                    position: 'absolute',
                    top: '3px',
                    right: settings.maintenanceMode ? '3px' : '25px',
                    transition: 'all 0.3s',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)'
                  }} />
                </div>
                <label style={{
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  cursor: 'pointer',
                  flex: 1,
                  marginRight: '12px'
                }}
                  onClick={() => handleChange('maintenanceMode', !settings.maintenanceMode)}
                >
                  وضع الصيانة
                </label>
              </div>

              {settings.maintenanceMode && (
                <div>
                  <label style={{
                    display: 'block',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#2B2F3F',
                    marginBottom: '8px',
                    textAlign: 'right'
                  }}>
                    رسالة الصيانة
                  </label>
                  <textarea
                    value={settings.maintenanceMessage || ''}
                    onChange={(e) => handleChange('maintenanceMessage', e.target.value)}
                    placeholder="نعمل على تحسين التطبيق لتقديم تجربة أفضل. سنعود قريباً!"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid rgba(102, 106, 122, 0.22)',
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '14px',
                      outline: 'none',
                      minHeight: '80px',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                    dir="rtl"
                  />
                </div>
              )}

              {/* Android Update Settings */}
              <div style={{ paddingTop: '16px', borderTop: '1px solid rgba(0, 0, 0, 0.06)' }}>
                <h4 style={{
                  fontFamily: 'El Messiri, sans-serif',
                  fontSize: '15px',
                  fontWeight: 700,
                  color: '#2B2F3F',
                  marginBottom: '16px',
                  textAlign: 'right',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  إعدادات Android
                </h4>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div style={{
                    width: '48px',
                    height: '26px',
                    borderRadius: '13px',
                    border: 'none',
                    backgroundColor: settings.updateRequiredAndroid ? '#E96A3A' : 'rgba(176, 179, 187, 0.3)',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.3s'
                  }}
                    onClick={() => handleChange('updateRequiredAndroid', !settings.updateRequiredAndroid)}
                  >
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#FFFFFF',
                      position: 'absolute',
                      top: '3px',
                      right: settings.updateRequiredAndroid ? '3px' : '25px',
                      transition: 'all 0.3s',
                      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)'
                    }} />
                  </div>
                  <label style={{
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#2B2F3F',
                    cursor: 'pointer',
                    flex: 1,
                    marginRight: '12px'
                  }}
                    onClick={() => handleChange('updateRequiredAndroid', !settings.updateRequiredAndroid)}
                  >
                    تحديث Android مطلوب
                  </label>
                </div>

                {settings.updateRequiredAndroid && (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{
                        display: 'block',
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#2B2F3F',
                        marginBottom: '8px',
                        textAlign: 'right'
                      }}>
                        رسالة التحديث (Android)
                      </label>
                      <textarea
                        value={settings.updateMessageAndroid || ''}
                        onChange={(e) => handleChange('updateMessageAndroid', e.target.value)}
                        placeholder="يتوفر إصدار جديد من التطبيق. يرجى التحديث للمتابعة."
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid rgba(102, 106, 122, 0.22)',
                          fontFamily: 'Cairo, sans-serif',
                          fontSize: '14px',
                          outline: 'none',
                          minHeight: '80px',
                          resize: 'vertical',
                          boxSizing: 'border-box'
                        }}
                        dir="rtl"
                      />
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{
                        display: 'block',
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#2B2F3F',
                        marginBottom: '8px',
                        textAlign: 'right'
                      }}>
                        رابط التحديث (Android)
                      </label>
                      <input
                        type="text"
                        value={settings.updateUrlAndroid || ''}
                        onChange={(e) => handleChange('updateUrlAndroid', e.target.value)}
                        placeholder="https://play.google.com/store/apps/details?id=..."
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid rgba(102, 106, 122, 0.22)',
                          fontFamily: 'Cairo, sans-serif',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#2B2F3F',
                        marginBottom: '8px',
                        textAlign: 'right'
                      }}>
                        الحد الأدنى لرقم النسخة (Android) - مثال: 1.0.0+1
                      </label>
                      <input
                        type="text"
                        value={settings.minimumAppVersionAndroid || '1.0.0+1'}
                        onChange={(e) => handleChange('minimumAppVersionAndroid', e.target.value)}
                        placeholder="1.0.0+1"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid rgba(102, 106, 122, 0.22)',
                          fontFamily: 'Cairo, sans-serif',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        dir="ltr"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* iOS Update Settings */}
              <div style={{ paddingTop: '20px', borderTop: '1px solid rgba(0, 0, 0, 0.06)' }}>
                <h4 style={{
                  fontFamily: 'El Messiri, sans-serif',
                  fontSize: '15px',
                  fontWeight: 700,
                  color: '#2B2F3F',
                  marginBottom: '16px',
                  textAlign: 'right',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  إعدادات iOS
                </h4>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div style={{
                    width: '48px',
                    height: '26px',
                    borderRadius: '13px',
                    border: 'none',
                    backgroundColor: settings.updateRequiredIOS ? '#E96A3A' : 'rgba(176, 179, 187, 0.3)',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.3s'
                  }}
                    onClick={() => handleChange('updateRequiredIOS', !settings.updateRequiredIOS)}
                  >
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      backgroundColor: '#FFFFFF',
                      position: 'absolute',
                      top: '3px',
                      right: settings.updateRequiredIOS ? '3px' : '25px',
                      transition: 'all 0.3s',
                      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)'
                    }} />
                  </div>
                  <label style={{
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#2B2F3F',
                    cursor: 'pointer',
                    flex: 1,
                    marginRight: '12px'
                  }}
                    onClick={() => handleChange('updateRequiredIOS', !settings.updateRequiredIOS)}
                  >
                    تحديث iOS مطلوب
                  </label>
                </div>

                {settings.updateRequiredIOS && (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{
                        display: 'block',
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#2B2F3F',
                        marginBottom: '8px',
                        textAlign: 'right'
                      }}>
                        رسالة التحديث (iOS)
                      </label>
                      <textarea
                        value={settings.updateMessageIOS || ''}
                        onChange={(e) => handleChange('updateMessageIOS', e.target.value)}
                        placeholder="يتوفر إصدار جديد من التطبيق. يرجى التحديث للمتابعة."
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid rgba(102, 106, 122, 0.22)',
                          fontFamily: 'Cairo, sans-serif',
                          fontSize: '14px',
                          outline: 'none',
                          minHeight: '80px',
                          resize: 'vertical',
                          boxSizing: 'border-box'
                        }}
                        dir="rtl"
                      />
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{
                        display: 'block',
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#2B2F3F',
                        marginBottom: '8px',
                        textAlign: 'right'
                      }}>
                        رابط التحديث (iOS)
                      </label>
                      <input
                        type="text"
                        value={settings.updateUrlIOS || ''}
                        onChange={(e) => handleChange('updateUrlIOS', e.target.value)}
                        placeholder="https://apps.apple.com/app/id..."
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid rgba(102, 106, 122, 0.22)',
                          fontFamily: 'Cairo, sans-serif',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        dir="ltr"
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#2B2F3F',
                        marginBottom: '8px',
                        textAlign: 'right'
                      }}>
                        الحد الأدنى لرقم النسخة (iOS) - مثال: 1.0.0+1
                      </label>
                      <input
                        type="text"
                        value={settings.minimumAppVersionIOS || '1.0.0+1'}
                        onChange={(e) => handleChange('minimumAppVersionIOS', e.target.value)}
                        placeholder="1.0.0+1"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid rgba(102, 106, 122, 0.22)',
                          fontFamily: 'Cairo, sans-serif',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                        dir="ltr"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Countries Management Section */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '32px',
          border: '1px solid rgba(233, 106, 58, 0.15)',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
          boxSizing: 'border-box',
          marginTop: '24px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <h2 style={{
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              color: '#2B2F3F',
              margin: 0
            }}>
              إدارة البلدان
            </h2>
            <button
              onClick={handleAddCountry}
              style={{
                padding: '10px 20px',
                borderRadius: '12px',
                border: 'none',
                backgroundColor: '#E96A3A',
                color: '#FFFFFF',
                fontFamily: 'El Messiri, sans-serif',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#d85a2a';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#E96A3A';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              إضافة بلد
            </button>
          </div>

          {loadingCountries ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              color: '#666A7A',
              fontFamily: 'Cairo, sans-serif'
            }}>
              جاري التحميل...
            </div>
          ) : countries.length === 0 ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              color: '#666A7A',
              fontFamily: 'Cairo, sans-serif'
            }}>
              لا توجد بلدان
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              {countries.map((country) => (
                <div
                  key={country.id}
                  style={{
                    backgroundColor: '#F9F9F9',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}
                >
                  {country.imageUrl && (
                    <img
                      src={country.imageUrl}
                      alt={country.name}
                      style={{
                        width: '100%',
                        height: '120px',
                        objectFit: 'cover',
                        borderRadius: '8px'
                      }}
                    />
                  )}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    {country.logoImage && (
                      <img
                        src={country.logoImage}
                        alt={`${country.name} logo`}
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'contain',
                          borderRadius: '4px'
                        }}
                      />
                    )}
                    <h3 style={{
                      fontFamily: 'El Messiri, sans-serif',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#2B2F3F',
                      margin: 0,
                      flex: 1
                    }}>
                      {country.name}
                    </h3>
                  </div>
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    marginTop: 'auto'
                  }}>
                    <button
                      onClick={() => handleEditCountry(country)}
                      style={{
                        flex: 1,
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(233, 106, 58, 0.3)',
                        backgroundColor: 'transparent',
                        color: '#E96A3A',
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      تعديل
                    </button>
                    <button
                      onClick={() => handleDeleteCountry(country.id)}
                      style={{
                        flex: 1,
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: '#F27D72',
                        color: '#FFFFFF',
                        fontFamily: 'Cairo, sans-serif',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      حذف
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Save Button */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
          gap: '12px',
          marginTop: '24px'
        }}>
          <button
            onClick={handleReset}
            disabled={saving}
            style={{
              padding: '12px 24px',
              borderRadius: '12px',
              border: '1px solid rgba(102, 106, 122, 0.3)',
              backgroundColor: saving ? '#f5f5f5' : 'transparent',
              color: saving ? '#999' : '#666A7A',
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '14px',
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              height: '48px',
              boxSizing: 'border-box',
              opacity: saving ? 0.6 : 1
            }}
          >
            إعادة تعيين
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '12px 32px',
              borderRadius: '12px',
              border: 'none',
              backgroundColor: saving ? '#cccccc' : '#E96A3A',
              color: '#FFFFFF',
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '14px',
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              height: '48px',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
          >
            {saving ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '2px solid #FFFFFF',
                  borderRadius: '50%',
                  animation: 'spin 0.6s linear infinite'
                }}></div>
                جاري الحفظ...
              </>
            ) : (
              'حفظ التغييرات'
            )}
          </button>
        </div>
      </div>

      {/* Country Modal */}
      {showCountryModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(43, 47, 63, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          animation: 'fadeIn 0.3s ease-out'
        }}
          onClick={() => !savingCountry && setShowCountryModal(false)}
        >
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '24px',
            padding: '40px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto',
            border: '1px solid rgba(233, 106, 58, 0.15)',
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.2)',
            animation: 'slideUp 0.3s ease-out'
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '20px',
              fontWeight: 700,
              color: '#2B2F3F',
              marginBottom: '24px',
              textAlign: 'right',
              margin: '0 0 24px 0'
            }}>
              {editingCountry ? 'تعديل البلد' : 'إضافة بلد جديد'}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  اسم البلد *
                </label>
                <input
                  type="text"
                  value={countryForm.name}
                  onChange={(e) => handleCountryFormChange('name', e.target.value)}
                  placeholder="مثال: السعودية"
                  disabled={savingCountry}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: savingCountry ? '#f5f5f5' : '#FFFFFF'
                  }}
                  dir="rtl"
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  صورة البلد
                </label>
                {countryForm.imagePreview && (
                  <img
                    src={countryForm.imagePreview}
                    alt="Preview"
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      marginBottom: '12px'
                    }}
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleCountryFormChange('imageFile', e.target.files[0])}
                  disabled={savingCountry}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: savingCountry ? '#f5f5f5' : '#FFFFFF'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2B2F3F',
                  marginBottom: '8px',
                  textAlign: 'right'
                }}>
                  شعار البلد
                </label>
                {countryForm.logoPreview && (
                  <img
                    src={countryForm.logoPreview}
                    alt="Logo Preview"
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'contain',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      backgroundColor: '#F9F9F9',
                      padding: '8px'
                    }}
                  />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleCountryFormChange('logoFile', e.target.files[0])}
                  disabled={savingCountry}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: savingCountry ? '#f5f5f5' : '#FFFFFF'
                  }}
                />
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '8px'
              }}>
                <button
                  onClick={() => {
                    setShowCountryModal(false);
                    setEditingCountry(null);
                    setCountryForm({
                      name: '',
                      imageFile: null,
                      logoFile: null,
                      imagePreview: null,
                      logoPreview: null
                    });
                  }}
                  disabled={savingCountry}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    backgroundColor: savingCountry ? '#f5f5f5' : 'transparent',
                    color: savingCountry ? '#999' : '#666A7A',
                    fontFamily: 'El Messiri, sans-serif',
                    fontSize: '14px',
                    cursor: savingCountry ? 'not-allowed' : 'pointer',
                    fontWeight: 500
                  }}
                >
                  إلغاء
                </button>
                <button
                  onClick={handleSaveCountry}
                  disabled={savingCountry || !countryForm.name.trim()}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '12px',
                    border: 'none',
                    backgroundColor: (savingCountry || !countryForm.name.trim()) ? '#cccccc' : '#E96A3A',
                    color: '#FFFFFF',
                    fontFamily: 'El Messiri, sans-serif',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: (savingCountry || !countryForm.name.trim()) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s',
                    opacity: (savingCountry || !countryForm.name.trim()) ? 0.7 : 1
                  }}
                >
                  {savingCountry ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid #FFFFFF',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite'
                      }}></div>
                      جاري الحفظ...
                    </>
                  ) : (
                    editingCountry ? 'تحديث' : 'إضافة'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default الإعدادات;
